import Vue from 'vue';
import App from './App.vue';
import router from './routing/router';
import store from './vuex/store';
import moment from 'moment';
import 'moment/locale/de-ch';
import 'moment/locale/en-gb';
import Buefy from 'buefy';
import VCalendar from 'v-calendar';
import Multiselect from 'vue-multiselect';
import ClipLoader from 'vue-spinner/src/ClipLoader.vue';
import axios from 'axios';
import { useSentry } from '@/main.sentry';
import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';

import '@fortawesome/fontawesome-pro/css/all.min.css';
import '@fortawesome/fontawesome-pro/css/sharp-light.min.css';
import '@fortawesome/fontawesome-pro/css/sharp-regular.min.css';
import '@fortawesome/fontawesome-pro/css/sharp-solid.min.css';

// ===== vue-simple-calendar =====
import 'vue-simple-calendar/static/css/default.css';
import './assets/sass/appexcalendar.scss';

// ===== VCalendar =====
// https://docs.vcalendar.io/#welcome-to-v-calendar
import 'v-calendar/lib/v-calendar.min.css';

// ===== LOCALIZATION =====
// All localization is included here
import i18n from './localization/localization';

import VueClipboard from 'vue-clipboard2';
// ===== SASS =====
// Main Sass file
import './assets/sass/main.scss';
import './assets/sass/appextable.scss';
import './assets/sass/appexstyle.scss';
import './assets/sass/effects.scss';
import './assets/sass/spacing.scss';
import { initializeAuth } from '@/helpers/auth/initializeAuth';
import { goToMainLink, goToNativeLink } from '@/helpers/routing/links';
import { useAuthScopes } from '@/lib/useAuthScopes';

// Check if necessary .envs are set
const envsToCheck = [
    'VUE_APP_BASE_URL',
    'VUE_APP_BASE_DOMAIN_URL',
    'VUE_APP_API_URL',
    'VUE_APP_V2_API_URL',
    'VUE_APP_BACKEND_BASE_URL',
    'VUE_APP_DEBUG',
    'VUE_APP_STATE_KEY',
    'VUE_APP_SENTRY_ENVIRONMENT',
    'VUE_APP_SENTRY_ENABLED',
    'VUE_APP_SENTRY_DSN',
    'VUE_APP_SENTRY_TRACE_SAMPLE_RATE',
    'VUE_APP_SWITCHER_APPEX_URL',
    'VUE_APP_SWITCHER_ORDERS_URL',
    'VUE_APP_SWITCHER_ADMIN_URL',
    'VUE_APP_SWITCHER_NEW_MAIN_URL',
    'VUE_APP_LESSON_MAX_LEVEL',
];

let hasMissingEnv = false;

for (const env of envsToCheck) {
    const value = process.env[env];

    if (!value && value !== 'false') {
        hasMissingEnv = true;
        console.error(`Missing env: ${env} - please check your .env file!`);
    }
}

if (hasMissingEnv) {
    throw new Error('Missing environment variables');
}

useSentry(Vue, router);

// ===== COMPONENTS (global) =====
Vue.component('multiselect', Multiselect);
Vue.component('clip-loader', ClipLoader);
Vue.component('vue-cropper', Vue.extend(VueCropper));

axios.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        console.log('401 interceptor error', error);

        if (error.response && error.response.status === 401) {
            // goToNativeLink('/auth/logout');
        }

        return Promise.reject(error);
    }
);

axios.defaults.baseURL = process.env.VUE_APP_API_URL;

const token = localStorage.getItem('api_token');
const { activeScope } = useAuthScopes();

export const axiosV2 = axios.create({
    baseURL: process.env.VUE_APP_V2_API_URL,
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(activeScope.value && activeScope.value.company ? { 'X-Company-Id': activeScope.value.company.id } : {}),
    },
});

axiosV2.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        console.log('v2 401 interceptor error', error);

        if (error.response && error.response.status === 401) {
            // goToMainLink('auth/login');
        }

        return Promise.reject(error);
    }
);

initializeAuth().then(() => {
    Vue.use(VCalendar, {
        firstDayOfWeek: 2, // Monday
        attrs: [
            {
                key: 'today',
                highlight: {
                    backgroundColor: '#ff8080',
                },
                // Just use a normal style
                contentStyle: {
                    color: '#000000',
                },
                dates: new Date(2018, 0, 1),
            },
        ],
        themeStyles: {
            wrapper: {
                background: 'linear-gradient(to bottom right, #ff5050, #ff66b3)',
                color: '#fafafa',
            },
        },
        formats: {
            title: 'MMM YYYY',
            weekdays: 'W',
            navMonths: 'MMM',
            input: ['DD.MM.YYYY'],
            dayPopover: 'DD.MM.YYYY',
            data: ['DD.MM.YYYY'],
        },
    });

    // ===== VUE CONFIG VARS =====
    // noinspection JSUnusedGlobalSymbols
    Vue.prototype.$version = '3.6.1.240827';
    Vue.config.productionTip = false;

    // ===== BUEFY =====
    // Vue JS Components for Bulma
    Vue.use(Buefy, {
        defaultIconPack: 'fas', // Maybe change to fas (new one)
    });

    // ===== VueClipboard =====
    // Vue.js Components for copying to Clipboard
    Vue.use(VueClipboard);

    // ===== FILTERS =====

    Vue.filter('truncate', (text: string, stop: number, clamp?: string) => {
        return text.slice(0, stop) + (stop < text.length ? clamp || '...' : '');
    });

    // Change Date format
    Vue.filter('formatDateTime', function (value) {
        if (value) {
            return moment(String(value)).format('DD.MM.YYYY HH:mm');
        }
    });

    Vue.filter('formatDate', function (value) {
        if (value) {
            return moment(String(value)).format('DD.MM.YYYY');
        }
    });

    Vue.filter('fromAgo', ({ date, locale }) => {
        if (date) {
            if (locale === 'de') {
                moment.locale('de-ch');
            } else {
                moment.locale('en-gb');
            }
            return moment(String(date)).fromNow();
        }
    });

    Vue.prototype.APP_BASE_URL = process.env.VUE_APP_BASE_URL;
    Vue.prototype.LESSON_MAX_LEVEL = process.env.VUE_APP_LESSON_MAX_LEVEL;
    Vue.config.devtools = process.env.VUE_APP_DEBUG === 'true';

    // noinspection JSUnusedGlobalSymbols
    Vue.prototype.BACKEND_STORAGE_PATH = process.env.VUE_APP_BACKEND_STORAGE_PATH;

    // ===== VUE =====
    // Create Vue Object and add all necessary parts
    new Vue({
        i18n,
        router,
        store,
        render: (h) => h(App),
    }).$mount('#app');
});
